import React, { useState } from 'react';
import { Pressable, View } from 'react-native';
import dayjs from 'dayjs';
import { useNavigation } from '@react-navigation/native';
import Icon from '../Icon';
import Table from '../Table';
import {
  AdminOrganizationRead,
  AdminTopicRead,
  GetAdminHelpdeskCategoriesParams,
  HelpdeskCategories,
} from '../../client/interfaces';
import { useMessage } from '../../contexts/Messages/MessageContext';
import svgIcons from '../../assets';
import Modal from '../Modal';
import TableCell from '../TableCell';
import client from '../../client/client';
import { AppNavigationProp, ScreenName } from '../../navigation/types';
import { useAppTranslation } from '../../hooks/useAppTranslation';
import { ToggleSwitch } from '../../components/index';

interface Props {
  rows: AdminTopicRead[] | AdminOrganizationRead[];
  paramSetter: React.Dispatch<React.SetStateAction<GetAdminHelpdeskCategoriesParams>>;
  params: GetAdminHelpdeskCategoriesParams;
  refresh: () => Promise<void>;
  isLoading?: boolean;
  setPaginated?: React.Dispatch<React.SetStateAction<AdminTopicRead[] | AdminOrganizationRead[]>>;
  activeTab: HelpdeskCategories;
}

const HelpdeskCategoryTable: React.FC<Props> = ({
  rows,
  paramSetter,
  params,
  refresh,
  isLoading = false,
  setPaginated,
  activeTab,
}) => {
  const { t } = useAppTranslation();
  const navigation = useNavigation<AppNavigationProp>();

  const { setMessage } = useMessage();

  const [itemToDeactivate, setItemToDeactivate] = useState<
    AdminTopicRead | AdminOrganizationRead
  >();

  const toggleActivity = async (
    row: AdminTopicRead | AdminOrganizationRead,
    newIsActive: boolean,
  ) => {
    setPaginated((prevState) =>
      prevState.map((item) => (item.id === row.id ? { ...item, isActive: newIsActive } : item)),
    );
    try {
      if (activeTab === HelpdeskCategories.topics) {
        await client.patchHelpdeskTopicActivity(row.id, { isActive: newIsActive });
      } else {
        await client.patchHelpdeskOrganizationActivity(row.id, newIsActive);
      }
    } catch (e) {
      // rollback
      setPaginated((prevState) =>
        prevState.map((item) => (item.id === row.id ? { ...item, isActive: !newIsActive } : item)),
      );
      setMessage({ message: e.message, type: 'error' });
    }
  };

  return (
    <View className="z-[10]">
      <Table
        paramSetter={paramSetter}
        params={params}
        isLoading={isLoading}
        keyFieldName="id"
        cellClassNamesFunction={(row, columnKey) =>
          columnKey !== 'actions' && !(row as AdminTopicRead).isActive ? 'opacity-50' : ''
        }
        columns={[
          {
            key: 'name',
            label: t('admin:table_columns:helpdesk_topics.title'),
            sortable: true,
            templateFunction: (row) => <TableCell text={(row as AdminTopicRead).name} />,
          },
          {
            key: 'created_at_utc',
            label: t('admin:table_columns:common.created_at'),
            sortable: true,
            templateFunction: (row) => (
              <TableCell
                text={dayjs(row.createdAtUtc).format('YYYY. MM. DD.')}
                key={`created_at_utc-${row.id}`}
              />
            ),
          },
          {
            key: 'lastEditedBy',
            label: t('admin:table_columns:common.last_edited_by'),
            templateFunction: (row) => (
              <TableCell
                text={(row as AdminTopicRead).lastEditedBy}
                key={(row as AdminTopicRead).lastEditedBy}
              />
            ),
          },
          {
            key: 'active',
            label: t('admin:table_columns:common.active'),
            widthPercentage: 10,
            templateFunction: (row) => (
              <TableCell key={row.id} classNames="flex-row justify-start">
                <ToggleSwitch
                  value={(row as AdminTopicRead).isActive}
                  onPress={() => {
                    if ((row as AdminTopicRead).isActive) {
                      setItemToDeactivate(row as AdminTopicRead);
                    } else {
                      toggleActivity(row as AdminTopicRead, true);
                    }
                  }}
                />
              </TableCell>
            ),
          },
          {
            key: 'actions',
            label: '',
            widthPercentage: 10,
            templateFunction: (row) => (
              <TableCell key={`actions-${row.id}`} classNames="flex-row justify-end">
                <Pressable
                  onPress={() => {
                    if (activeTab === HelpdeskCategories.topics) {
                      navigation.navigate(ScreenName.AdminHelpdeskTopicEditScreen, {
                        topicId: (row as AdminTopicRead).id,
                      });
                    } else {
                      navigation.navigate(ScreenName.AdminHelpdeskOrganizationEditScreen, {
                        organizationId: (row as AdminOrganizationRead).id,
                      });
                    }
                  }}
                  className="items-center w-[24px] mr-4"
                >
                  <View className="cursor-pointer">
                    <Icon
                      icon={svgIcons.editIcon}
                      mobileSize={{ width: 24, height: 24 }}
                      webSize={{ width: 24, height: 24 }}
                    />
                  </View>
                </Pressable>
              </TableCell>
            ),
          },
        ]}
        rows={rows}
      />
      <Modal
        showModal={!!itemToDeactivate}
        title={
          activeTab === HelpdeskCategories.topics
            ? t('admin:helpdesk_categories:sure_to_deactivate_topic')
            : t('admin:helpdesk_categories:sure_to_deactivate_organization')
        }
        description={
          activeTab === HelpdeskCategories.topics
            ? t('admin:helpdesk_categories:deactivate_topic_description')
            : t('admin:helpdesk_categories:deactivate_organization_description')
        }
        callback={() => {
          toggleActivity(itemToDeactivate, false);
          setItemToDeactivate(null);
        }}
        closeModal={() => {
          setItemToDeactivate(null);
        }}
        yesText={t('admin:helpdesk_categories:yes_deactivate')}
        cancelText={t('admin:helpdesk_categories:no')}
      />
    </View>
  );
};
export default HelpdeskCategoryTable;
